/**
 * FmFormFlowPage chrome: navigation rails, the whole-flow and per-section state
 * surfaces, the stage story header, and the stage state NOTICE.
 *
 * Split from `FmFormFlowPage.tsx` so neither file crosses the 500-line gate. The
 * page root keeps the composition and the interaction predicates; everything
 * that paints chrome around the capability slot lives here.
 */
import React from 'react';
import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmChip } from './FmChip';
import { FmHeading, FmInline, FmStack, FmText } from './FmComposition';
import { FmEmpty, FmSpinner } from './FmFeedback';
import { FmUnlockPreview } from './FmUnlockPreview';
import { activeSectionItem, sectionStateAllowsInteraction } from './FmFormFlowPage.helpers';
import type {
  FmFormFlowLinearItemDescriptor,
  FmFormFlowPageDescriptor,
} from './FmFormFlowPage.types';

const LINEAR_STATUS_TONE = {
  complete: 'success',
  current: 'brand',
  available: 'mute',
  blocked: 'warn',
} as const;

export function LinearNavigation({
  descriptor,
}: {
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement | null {
  if (descriptor.navigation.kind !== 'linear') return null;
  const navigation = descriptor.navigation;
  const activeIndex = navigation.items.findIndex((item) => item.id === navigation.activeId);
  return (
    <nav className="fm-form-flow__linear-nav" aria-label={navigation.ariaLabel}>
      <ol>
        {navigation.items.map((item, index) => {
          /* A locked flow does not navigate. The nav sits OUTSIDE the disabled
             fieldset, so without this the owner could walk to another stage of a
             flow they have not paid for — and the previewable-control rule,
             which validates the ACTIVE stage, would throw during the resulting
             render and take the unlock CTA down with the page. */
          const canVisit =
            descriptor.state !== 'locked' &&
            (item.status === 'current' || (item.status === 'complete' && index < activeIndex));
          const content = (
            <>
              <span aria-hidden="true">{index + 1}</span>
              <span>
                <FmText weight="bold">{item.label}</FmText>
                {item.description ? (
                  <FmText as="small" size="caption" tone="muted">
                    {item.description}
                  </FmText>
                ) : null}
              </span>
              {item.statusLabel ? (
                <FmChip variant={LINEAR_STATUS_TONE[item.status]}>{item.statusLabel}</FmChip>
              ) : null}
            </>
          );
          return (
            <li key={item.id} data-status={item.status}>
              {item.status === 'current' ? (
                <div aria-current="step" data-testid={`form-flow-step-${item.id}`}>
                  {content}
                </div>
              ) : canVisit ? (
                <button
                  type="button"
                  title={navigation.invalidationMessage}
                  onClick={() =>
                    descriptor.onEvent?.({
                      kind: 'navigate',
                      itemId: item.id,
                      invalidatesFollowing: true,
                    })
                  }
                  data-testid={`form-flow-step-${item.id}`}
                >
                  {content}
                </button>
              ) : (
                <div
                  aria-disabled="true"
                  title={item.disabledReason}
                  data-testid={`form-flow-step-${item.id}`}
                >
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function SectionNavigation({
  descriptor,
}: {
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement | null {
  if (descriptor.navigation.kind !== 'sections') return null;
  return (
    <nav
      className="fm-form-flow__section-nav" /* design-rescue-ok: form-flow section navigation is an archetype-owned rail, not route-local composition. */
      aria-label={descriptor.navigation.ariaLabel}
      data-testid="form-flow-section-nav"
    >
      <ul>
        {descriptor.navigation.items.map((item) => {
          const content = (
            <>
              <strong>{item.label}</strong>
              <small>{item.stateLabel}</small>
            </>
          );
          return (
            <li key={item.id}>
              {item.state === 'locked' || descriptor.state === 'locked' ? (
                <span aria-disabled="true" data-testid={`form-flow-section-${item.id}`}>
                  {content}
                </span>
              ) : (
                <a
                  href={`#${descriptor.id}-${item.id}`}
                  aria-current={item.id === descriptor.navigation.activeId ? 'page' : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    descriptor.onEvent?.({
                      kind: 'navigate',
                      itemId: item.id,
                      invalidatesFollowing: false,
                    });
                  }}
                  data-testid={`form-flow-section-${item.id}`}
                >
                  {content}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * The action a state surface names. It stays usable while the state inhibits the
 * ordinary toolbar — otherwise the surface offers an exit the owner cannot take
 * (mirrors FmOperationalWorkspaceStateSurfaces.StateAction).
 */
export function StateAction({
  descriptor,
  actionId,
}: {
  descriptor: FmFormFlowPageDescriptor;
  actionId: string;
}): React.ReactElement | null {
  const action = descriptor.actions.find((item) => item.id === actionId);
  if (!action) return null;
  return (
    <FmButton
      variant={action.variant ?? 'primary'}
      /* Defence at the render boundary, not trust in the validator alone. The
         validator refuses a disabled action here, but this component must
         still pass `disabled` through, so a single weakened rule upstream
         cannot show a refused action as a live button. */
      disabled={action.disabled}
      title={action.disabledReason}
      onClick={() => descriptor.onEvent?.({ kind: 'action', actionId: action.id })}
      data-testid={`form-flow-state-action-${action.id}`}
    >
      {action.label}
    </FmButton>
  );
}

/**
 * Every non-ready state is a designed surface with its own tone, copy and
 * resolving control, not one grey sentence centred in a dead band.
 */
export function StateSurface({
  descriptor,
}: {
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement | null {
  if (descriptor.state === 'ready') return null;
  if (descriptor.state === 'loading') {
    return (
      <output className="fm-form-flow__state">
        <FmSpinner tip={descriptor.stateLabel} />
      </output>
    );
  }
  if (descriptor.state === 'error') {
    return (
      <div className="fm-form-flow__state-notice" data-testid="form-flow-state-error">
        <FmBanner
          tone="error"
          title={descriptor.error.scopeLabel}
          description={descriptor.error.description}
          action={<StateAction descriptor={descriptor} actionId={descriptor.error.retryActionId} />}
        />
      </div>
    );
  }
  if (descriptor.state === 'locked') {
    const unlock = descriptor.locked.unlock;
    return (
      <div className="fm-form-flow__state-notice" data-testid="form-flow-state-locked">
        <FmBanner
          tone="info"
          title={descriptor.locked.title}
          /* Never the title twice. A caller whose served reason IS its status
             label would otherwise print one sentence as both. */
          description={
            descriptor.locked.description === descriptor.locked.title
              ? undefined
              : descriptor.locked.description
          }
        />
        {unlock ? (
          <FmUnlockPreview
            preview={unlock}
            onUnlock={() =>
              descriptor.onEvent?.({ kind: 'action', actionId: unlock.unlockActionId })
            }
            data-testid="form-flow-unlock-preview"
          />
        ) : null}
      </div>
    );
  }
  return (
    <output className="fm-form-flow__state" data-testid="form-flow-state-empty">
      <FmEmpty
        description={descriptor.empty.description}
        action={<StateAction descriptor={descriptor} actionId={descriptor.empty.primaryActionId} />}
      />
    </output>
  );
}

export function SectionStateSurface({
  descriptor,
}: {
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement | null {
  const item = activeSectionItem(descriptor);
  if (!item || sectionStateAllowsInteraction(item.state)) return null;
  if (item.state === 'loading' || item.state === 'saving') {
    return (
      <output className="fm-form-flow__state" aria-live="polite">
        <FmSpinner tip={item.stateLabel} />
      </output>
    );
  }
  if (item.state === 'error') {
    return (
      <div className="fm-form-flow__state">
        <FmBanner tone="error" title={item.stateLabel} />
      </div>
    );
  }
  return (
    <output className="fm-form-flow__state">
      <FmEmpty description={item.stateLabel} />
    </output>
  );
}

export function LinearStageStory({
  item,
}: {
  item: FmFormFlowLinearItemDescriptor;
}): React.ReactElement {
  return (
    <header className="fm-form-flow__stage-story" data-conversion-role={item.story.conversionRole}>
      <span className="fm-form-flow__eyebrow">{item.label}</span>
      <FmHeading level={2} id={`form-flow-heading-${item.id}`} tabIndex={-1}>
        {item.story.ownerQuestion}
      </FmHeading>
      <FmText tone="muted">{item.story.literalAnswer}</FmText>
    </header>
  );
}

/**
 * Every stage outcome is a NOTICE — tone, icon, container — and never a grey
 * sentence centred in a dead band. A zero result and a capacity refusal used to
 * take the `FmEmpty` branch and print as unstyled centred text above the heading
 * of the control that produced them, while the error state next door rendered
 * correctly; the shape the error had is now the shape they all have
 */
const STAGE_STATE_TONE = {
  empty: 'info',
  partial: 'warning',
  stale: 'warning',
  locked: 'warning',
  error: 'error',
} as const;

export function LinearStageStateSurface({
  descriptor,
  item,
}: {
  descriptor: FmFormFlowPageDescriptor;
  item: FmFormFlowLinearItemDescriptor;
}): React.ReactElement | null {
  const story = item.story;
  if (story.state === 'ready') return null;
  const copy = story.stateCopy[story.state];
  if (story.state === 'loading')
    return (
      <output className="fm-form-flow__stage-state">
        <FmSpinner tip={copy} />
      </output>
    );
  const resolvingActionId = story.stateAnchor?.resolvingActionId;
  return (
    <div
      className="fm-form-flow__stage-notice"
      data-testid={`form-flow-stage-state-${story.state}`}
    >
      <FmBanner
        tone={STAGE_STATE_TONE[story.state]}
        title={copy}
        action={
          resolvingActionId ? (
            <StateAction descriptor={descriptor} actionId={resolvingActionId} />
          ) : undefined
        }
      />
    </div>
  );
}

export function FlowHeader({
  descriptor,
}: {
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  return (
    <header className="fm-form-flow__header">
      <FmStack gap="sm">
        {descriptor.backLabel ? (
          <FmInline>
            <FmButton variant="link" onClick={() => descriptor.onEvent?.({ kind: 'back' })}>
              {descriptor.backLabel}
            </FmButton>
          </FmInline>
        ) : null}
        <span className="fm-form-flow__eyebrow">{descriptor.navigation.ariaLabel}</span>
        <FmHeading level={1}>{descriptor.title}</FmHeading>
        {descriptor.description ? <FmText tone="muted">{descriptor.description}</FmText> : null}
      </FmStack>
    </header>
  );
}
